import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { makeStyles, withStyles } from '@material-ui/styles';
import {
  Box,
  Paper,
  Typography,
  Avatar,
  CircularProgress,
  Badge,
  IconButton
} from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { getInitials } from '../../helpers';
import { getTimeFromXDisplay } from '../../helpers';
import MessageInput from '../MessageInput';
import DoneAllIcon from '@material-ui/icons/DoneAll';

// Styled badge for online status indicator
const OnlineBadge = withStyles((theme) => ({
  badge: {
    backgroundColor: '#44b700',
    color: '#44b700',
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
    '&::after': {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      borderRadius: '50%',
      animation: '$ripple 1.2s infinite ease-in-out',
      border: '1px solid currentColor',
      content: '""'
    }
  },
  '@keyframes ripple': {
    '0%': {
      transform: 'scale(.8)',
      opacity: 1
    },
    '100%': {
      transform: 'scale(2.4)',
      opacity: 0
    }
  }
}))(Badge);

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: theme.palette.background.default
  },
  header: {
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper
  },
  backButton: {
    marginRight: theme.spacing(1)
  },
  messagesContainer: {
    flex: 1,
    overflowY: 'auto',
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column'
  },
  messageWrapper: {
    display: 'flex',
    marginBottom: theme.spacing(2),
    '&.own': {
      justifyContent: 'flex-end'
    }
  },
  messageContent: {
    display: 'flex',
    maxWidth: '70%',
    '&.own': {
      flexDirection: 'row-reverse'
    }
  },
  avatar: {
    width: 32,
    height: 32,
    margin: theme.spacing(0, 1),
    backgroundColor: theme.palette.primary.main
  },
  messageBubble: {
    padding: theme.spacing(1.5, 2),
    borderRadius: 16,
    backgroundColor: theme.palette.background.paper,
    '&.own': {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText
    }
  },
  messageText: {
    wordBreak: 'break-word',
    whiteSpace: 'pre-wrap'
  },
  messageFooter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(0.5)
  },
  timestamp: {
    fontSize: '0.7rem',
    opacity: 0.7
  },
  readIcon: {
    fontSize: '1rem',
    opacity: 0.7
  },
  typingIndicator: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0.5, 2),
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.background.paper,
    borderTop: `1px solid ${theme.palette.divider}`,
    minHeight: 28
  },
  typingIndicatorPlaceholder: {
    minHeight: 28,
    backgroundColor: theme.palette.background.paper,
    borderTop: `1px solid ${theme.palette.divider}`
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%'
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    textAlign: 'center',
    padding: theme.spacing(4)
  }
}));

const ChatView = ({
  conversation,
  messages,
  currentUserId,
  loading,
  onSendMessage,
  typingUsers,
  isOtherUserOnline,
  onBack,
  isMobile
}) => {
  const classes = useStyles();
  const messagesContainerRef = useRef(null);
  const prevMessagesLengthRef = useRef(0);

  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  };

  // Only scroll when new messages are added, not on every render
  useEffect(() => {
    const currentLength = messages.length;
    const prevLength = prevMessagesLengthRef.current;
    
    // Scroll only if messages count increased (new message added)
    if (currentLength > prevLength) {
      scrollToBottom();
    }
    
    prevMessagesLengthRef.current = currentLength;
  }, [messages.length]);

  if (!conversation) {
    return (
      <Box className={classes.emptyState}>
        <Typography variant="h6" color="textSecondary">
          Select a conversation to start messaging
        </Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress />
      </Box>
    );
  }

  const otherUserName = conversation.otherUserName || conversation.companyName || 'Unknown User';
  const otherUserImageUrl = conversation.otherUserImageUrl || conversation.companyLogoUrl;
  const currentUserImageUrl = conversation.currentUserImageUrl;
  const recipientUserId =
    conversation.companyUserId === currentUserId
      ? conversation.candidateUserId
      : conversation.companyUserId;

  // Check if anyone is typing
  const isTyping =
    typingUsers &&
    Object.keys(typingUsers).some((userId) => userId !== currentUserId);

  return (
    <Box className={classes.root}>
      {/* Header */}
      <Paper className={classes.header} elevation={1}>
        <Box display="flex" alignItems="center">
          {/* Back button for mobile */}
          {onBack && (
            <IconButton 
              className={classes.backButton} 
              onClick={onBack}
              size="small"
            >
              <ArrowBackIcon />
            </IconButton>
          )}
          <OnlineBadge
            overlap="circular"
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            variant="dot"
            invisible={!isOtherUserOnline}>
            <Avatar 
              className={classes.avatar}
              src={otherUserImageUrl || undefined}
            >
              {!otherUserImageUrl && getInitials(otherUserName)}
            </Avatar>
          </OnlineBadge>
          <Box ml={1}>
            <Typography variant={isMobile ? "subtitle1" : "h6"}>{otherUserName}</Typography>
            <Typography 
              variant="caption" 
              style={{ color: isOtherUserOnline ? '#44b700' : 'inherit' }}
            >
              {isOtherUserOnline ? 'Online' : 'Offline'}
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Messages */}
      <Box className={classes.messagesContainer} ref={messagesContainerRef}>
        {messages.length === 0 ? (
          <Box className={classes.emptyState}>
            <Typography variant="body2" color="textSecondary">
              No messages yet. Start the conversation!
            </Typography>
          </Box>
        ) : (
          messages.map((message) => {
            const isOwnMessage = message.senderUserId === currentUserId;
            return (
              <Box
                key={message.id}
                className={`${classes.messageWrapper} ${
                  isOwnMessage ? 'own' : ''
                }`}>
                <Box
                  className={`${classes.messageContent} ${
                    isOwnMessage ? 'own' : ''
                  }`}>
                  <Avatar 
                    className={classes.avatar}
                    src={isOwnMessage ? (currentUserImageUrl || undefined) : (otherUserImageUrl || undefined)}
                  >
                    {isOwnMessage 
                      ? (!currentUserImageUrl && getInitials('You'))
                      : (!otherUserImageUrl && getInitials(otherUserName))
                    }
                  </Avatar>
                  <Box>
                    <Paper
                      className={`${classes.messageBubble} ${
                        isOwnMessage ? 'own' : ''
                      }`}
                      elevation={1}>
                      <Typography
                        variant="body2"
                        className={classes.messageText}>
                        {message.content}
                      </Typography>
                      <Box className={classes.messageFooter}>
                        <Typography
                          variant="caption"
                          className={classes.timestamp}>
                          {getTimeFromXDisplay(message.createdAt)}
                        </Typography>
                        {isOwnMessage && message.isRead && (
                          <DoneAllIcon className={classes.readIcon} />
                        )}
                      </Box>
                    </Paper>
                  </Box>
                </Box>
              </Box>
            );
          })
        )}
      </Box>

      {/* Typing Indicator - Fixed position above input */}
      {isTyping ? (
        <Box className={classes.typingIndicator}>
          <Typography variant="caption">{otherUserName} is typing...</Typography>
        </Box>
      ) : (
        <Box className={classes.typingIndicatorPlaceholder} />
      )}

      {/* Message Input */}
      <MessageInput
        onSendMessage={onSendMessage}
        conversationId={conversation.id}
        recipientUserId={recipientUserId}
      />
    </Box>
  );
};

ChatView.propTypes = {
  conversation: PropTypes.object,
  messages: PropTypes.array.isRequired,
  currentUserId: PropTypes.string.isRequired,
  loading: PropTypes.bool,
  onSendMessage: PropTypes.func.isRequired,
  typingUsers: PropTypes.object,
  isOtherUserOnline: PropTypes.bool,
  onBack: PropTypes.func,
  isMobile: PropTypes.bool
};

ChatView.defaultProps = {
  messages: [],
  loading: false,
  typingUsers: {},
  isOtherUserOnline: false,
  onBack: null,
  isMobile: false
};

export default ChatView;

